/**
 * Generic renderer for {@link SerializableDesign}, ported verbatim from the
 * application so moderation previews look exactly like the live widget. It is
 * also the XML-escaping choke-point — user-controlled slots are escaped before
 * touching markup. Keep in sync with
 * `app/projects/application/src/entities/designs/renderDesign.ts`.
 */

import { evalFormula } from "./formulaEval";
import type {
  DesignSlot,
  DesignList,
  DesignFragment,
  SerializableDesign,
} from "./serializableDesign";

export interface RenderData {
  values?: Record<string, string | number | undefined>;
  items?: Array<Record<string, string | number | undefined>>;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

const esc = (s: string): string =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const trunc = (s: string, n: number): string => {
  const t = String(s ?? "");
  return t.length <= n ? t : `${t.slice(0, Math.max(0, n - 1)).trimEnd()}…`;
};

const thousands = (n: number): string =>
  Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");

function renderSlot(
  slot: DesignSlot,
  raw: string | number | undefined,
): string {
  if (slot.kind === "color") {
    const c = typeof raw === "string" ? raw : "";
    return HEX.test(c) ? c : (slot.fallback ?? "#ffffff");
  }
  if (slot.kind === "number" || slot.kind === "pct") {
    const n = typeof raw === "number" ? raw : Number(raw);
    const v = Number.isFinite(n) ? n : 0;
    return slot.format === "thousands" ? thousands(v) : String(Math.round(v));
  }
  let s = raw == null ? (slot.fallback ?? "") : String(raw);
  if (typeof slot.cap === "number") s = trunc(s, slot.cap);
  const shouldEscape = slot.escape ?? slot.kind === "text";
  return shouldEscape ? esc(s) : s;
}

function fill(template: string, map: Record<string, string>): string {
  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (whole, name) =>
    name in map ? map[name] : whole,
  );
}

function buildVars(
  design: SerializableDesign,
  values: Record<string, string | number | undefined>,
): Record<string, number> {
  const vars: Record<string, number> = Object.create(null);
  vars.W = design.cardWidth;
  vars.H = design.cardHeight;
  for (const [k, v] of Object.entries(values)) {
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n)) vars[k] = n;
  }
  if (design.computed) {
    for (const [name, formula] of Object.entries(design.computed)) {
      const r = evalFormula(formula, vars);
      vars[name] = Number.isFinite(r) ? r : 0;
    }
  }
  return vars;
}

function resolveScalarMap(
  design: SerializableDesign,
  values: Record<string, string | number | undefined>,
  vars: Record<string, number>,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [k, v] of Object.entries(vars)) map[k] = String(v);
  if (design.slots) {
    for (const [name, slot] of Object.entries(design.slots)) {
      map[name] = renderSlot(slot, values[name]);
    }
  }
  return map;
}

function renderList(list: DesignList, items: RenderData["items"]): string {
  const recent = (items ?? []).slice(-list.maxRows);
  return recent
    .map((item, i) => {
      const rowMap: Record<string, string> = {
        ROW_INDEX: String(i),
        ROW_Y: String((list.baselineY ?? 0) + i * (list.rowHeight ?? 0)),
      };
      for (const [field, slot] of Object.entries(list.rowSlots)) {
        let raw = item[field];
        if (slot.kind === "color" && raw == null && list.palette?.length) {
          raw = list.palette[i % list.palette.length];
        }
        if (slot.kind === "color") {
          const c = typeof raw === "string" && HEX.test(raw) ? raw : undefined;
          rowMap[field] =
            c ??
            (list.palette?.length
              ? list.palette[i % list.palette.length]
              : (slot.fallback ?? "#ffffff"));
          continue;
        }
        rowMap[field] = renderSlot(slot, raw);
      }
      return fill(list.row, rowMap);
    })
    .join("");
}

function renderFragment(
  frag: DesignFragment,
  scalarMap: Record<string, string>,
  vars: Record<string, number>,
): string {
  if (frag.repeat) {
    const idxVar = frag.repeat.var ?? "j";
    const count = Math.max(0, Math.floor(evalFormula(frag.repeat.count, vars)));
    let out = "";
    for (let j = 0; j < count; j++) {
      const localVars = { ...vars, [idxVar]: j };
      const localMap = { ...scalarMap, [idxVar]: String(j) };
      if (frag.showIf && evalFormula(frag.showIf, localVars) === 0) continue;
      out += fill(frag.svg, localMap);
    }
    return out;
  }
  if (frag.showIf && evalFormula(frag.showIf, vars) === 0) return "";
  return fill(frag.svg, scalarMap);
}

export function renderDesign(
  design: SerializableDesign,
  data: RenderData = {},
): string {
  const values = data.values ?? {};
  const vars = buildVars(design, values);
  const scalarMap = resolveScalarMap(design, values, vars);

  let svg = design.frame;

  if (design.list && svg.includes("{{ROWS}}")) {
    svg = svg.replace("{{ROWS}}", renderList(design.list, data.items));
  }
  if (design.fragments) {
    const buckets: Record<string, string> = {};
    for (const frag of design.fragments) {
      const marker = frag.key ? `{{FRAGMENTS:${frag.key}}}` : "{{FRAGMENTS}}";
      buckets[marker] =
        (buckets[marker] ?? "") + renderFragment(frag, scalarMap, vars);
    }
    for (const [marker, markup] of Object.entries(buckets)) {
      svg = svg.replace(marker, markup);
    }
  }
  return fill(svg, scalarMap);
}

export function renderScrollSegment(
  design: SerializableDesign,
  data: RenderData = {},
): string {
  if (!design.scroll) return "";
  const values = data.values ?? {};
  const map: Record<string, string> = {};
  for (const [name, slot] of Object.entries(design.scroll.slots)) {
    map[name] = renderSlot(slot, values[name]);
  }
  return fill(design.scroll.segment, map);
}
